<script lang="ts">
  /**
   * The 1v1 lobby: create or join a room, then wait your turn.
   *
   * One of the two screens the design set never covered, so this is built from
   * the existing components rather than invented — the same panels, eyebrows and
   * action bar as everywhere else.
   *
   * Its real job is answering "what now?" at every point in an async match.
   * Async means half the time the honest answer is "nothing, they're up" — and
   * a screen that says that clearly beats one that looks broken.
   */
  import type { RoomView } from '../lib/room.svelte';
  import { waitingOnOpponent } from '../lib/room.svelte';

  interface Props {
    view: RoomView | null;
    busy: boolean;
    error: string;
    onCreate: (teamName: string) => void;
    onJoin: (code: string, teamName: string) => void;
    onDraft: () => void;
    onPlay: () => void;
    onResult: () => void;
    onExtend: () => void;
    onHome: () => void;
    ontoast: (message: string) => void;
  }
  let {
    view,
    busy,
    error,
    onCreate,
    onJoin,
    onDraft,
    onPlay,
    onResult,
    onExtend,
    onHome,
    ontoast,
  }: Props = $props();

  let teamName = $state('');
  let joinCode = $state('');
  let mode = $state<'choose' | 'join'>('choose');

  const named = $derived(teamName.trim().length > 0);
  const you = $derived(view ? (view.you === 'host' ? view.host : view.guest) : null);
  const them = $derived(view ? (view.you === 'host' ? view.guest : view.host) : null);
  const waiting = $derived(view ? waitingOnOpponent(view) : false);
  const yourTurnToPlay = $derived(
    view !== null &&
      (view.status === 'INNINGS_1' || view.status === 'INNINGS_2' || view.status === 'SUPER_OVER') &&
      view.awaiting === view.you,
  );
  const inSuperOver = $derived(view?.status === 'SUPER_OVER');

  const inviteUrl = $derived(view ? `${location.origin}/#join=${view.code}` : '');

  async function share(): Promise<void> {
    const text = `Pick your XI and take me on — room ${view!.code}`;
    try {
      // The native sheet is the whole point on a phone: it reaches WhatsApp,
      // which is where this invite is actually going.
      if (navigator.share) {
        await navigator.share({ title: 'pickthe11', text, url: inviteUrl });
        return;
      }
      await navigator.clipboard.writeText(inviteUrl);
      ontoast('Invite link copied');
    } catch {
      // Both APIs reject when the user dismisses the sheet. That is not an
      // error worth reporting.
    }
  }

  /** "in 4 hours", for a deadline nobody wants to read as a timestamp. */
  function remaining(at: number): string {
    const ms = at - Date.now();
    if (ms <= 0) return 'expired';
    const hours = Math.floor(ms / 3_600_000);
    if (hours >= 1) return `${hours} hour${hours === 1 ? '' : 's'} left`;
    const minutes = Math.max(1, Math.round(ms / 60_000));
    return `${minutes} minute${minutes === 1 ? '' : 's'} left`;
  }
</script>

<section class="screen active" aria-label="1 vs 1">
  <div class="body">
    {#if !view}
      <!-- ── entry ─────────────────────────────────────────────────────── -->
      <h2>1 vs 1</h2>
      <p class="muted">
        Both sides draft from the same squads in the same order, with the same re-spins. The only
        difference is who you pick.
      </p>

      <div class="field" style="margin-top:var(--space-5)">
        <label for="rt-name">Your team name</label>
        <input
          id="rt-name"
          bind:value={teamName}
          placeholder="e.g. Gully Legends"
          autocomplete="off"
          maxlength="24"
        />
        <p class="hint">This is what your opponent sees.</p>
      </div>

      {#if mode === 'join'}
        <div class="field" style="margin-top:var(--space-4)">
          <label for="rt-code">Room code</label>
          <input
            id="rt-code"
            bind:value={joinCode}
            placeholder="ABC123"
            autocomplete="off"
            maxlength="6"
            style="text-transform:uppercase"
          />
        </div>
      {/if}

      {#if error}
        <div class="card" style="margin-top:var(--space-4);border-left:3px solid var(--danger)">
          <p style="margin:0;font-size:var(--text-sm)">{error}</p>
        </div>
      {/if}
    {:else}
      <!-- ── an open room ──────────────────────────────────────────────── -->
      <p class="eyebrow">Room {view.code}</p>

      <div class="card">
        <div class="vs">
          <span>
            <span class="you-team">{you?.teamName ?? 'You'}</span><br />
            <span class="s">{you?.ready ? 'Squad in' : 'Drafting'}</span>
          </span>
          <span class="faint">vs</span>
          <span>
            <span class="opp-team">{them?.teamName ?? 'Waiting…'}</span><br />
            <span class="s">{them ? (them.ready ? 'Squad in' : 'Drafting') : 'Not joined'}</span>
          </span>
        </div>
      </div>

      {#if view.status === 'EXPIRED'}
        <div class="card" style="margin-top:var(--space-4);border-left:3px solid var(--danger)">
          <p style="margin:0;font-size:var(--text-sm)">
            This room ran out of time.{view.you === 'host' && !view.extended
              ? ' You can give it more.'
              : ' Ask the room creator to extend it.'}
          </p>
        </div>
      {:else}
        <p class="faint" style="margin-top:var(--space-3)">{remaining(view.expiresAt)}</p>
      {/if}

      {#if view.status === 'WAITING'}
        <p class="eyebrow" style="margin-top:var(--space-5)">Invite</p>
        <div class="card">
          <p style="margin:0 0 var(--space-2);font-size:var(--text-sm)">
            Send this code, or share the link.
          </p>
          <p class="year" style="margin:0;letter-spacing:.12em">{view.code}</p>
        </div>
      {/if}

      {#if view.innings.length > 0}
        <p class="eyebrow" style="margin-top:var(--space-5)">Score</p>
        {#each view.innings as innings (innings.side)}
          <div class="card">
            <strong>{innings.side === view.you ? (you?.teamName ?? 'You') : (them?.teamName ?? 'Opponent')}</strong>
            <span class="muted tabular">
              {innings.runs}/{innings.wickets}
              ({Math.floor(innings.legalBalls / 6)}.{innings.legalBalls % 6})
            </span>
          </div>
        {/each}
        {#if (view.status === 'INNINGS_2' || view.status === 'SUPER_OVER') && view.target}
          <p class="faint" style="margin-top:var(--space-2)">
            {view.awaiting === view.you ? 'You need' : 'They need'}
            <b class="tabular">{view.target}</b> to win.
          </p>
        {/if}
      {/if}

      {#if inSuperOver}
        <p class="eyebrow" style="margin-top:var(--space-5)">
          Super over{view.superOverIndex > 0 ? ` ${view.superOverIndex + 1}` : ''}
        </p>
        <div class="card">
          <p style="margin:0;font-size:var(--text-sm)">
            {view.superOverIndex > 0
              ? 'Still level. One over each, two wickets, again.'
              : 'Level after twenty overs each. One over each, two wickets, highest score wins.'}
          </p>
        </div>
        {#each view.superOvers as so, i (i)}
          <div class="card">
            <strong>{so.side === view.you ? (you?.teamName ?? 'You') : (them?.teamName ?? 'Opponent')}</strong>
            <span class="muted tabular">
              {so.runs}/{so.wickets} ({Math.floor(so.legalBalls / 6)}.{so.legalBalls % 6})
            </span>
          </div>
        {/each}
      {/if}

      {#if waiting && view.status !== 'EXPIRED'}
        <div class="card" style="margin-top:var(--space-4)">
          <p style="margin:0;font-size:var(--text-sm)">
            {#if view.status === 'WAITING'}
              Waiting for someone to join.
            {:else if view.status === 'DRAFTING'}
              Waiting for {them?.teamName ?? 'your opponent'} to pick their side.
            {:else if view.status === 'SUPER_OVER'}
              Waiting for {them?.teamName ?? 'your opponent'} to play their super over.
            {:else}
              Waiting for {them?.teamName ?? 'your opponent'} to play their innings.
            {/if}
            You'll see it here — nothing to keep open.
          </p>
        </div>
      {/if}

      {#if error}
        <div class="card" style="margin-top:var(--space-4);border-left:3px solid var(--danger)">
          <p style="margin:0;font-size:var(--text-sm)">{error}</p>
        </div>
      {/if}
    {/if}
  </div>

  <div class="actions">
    {#if !view}
      {#if mode === 'choose'}
        <button class="btn-primary" disabled={!named || busy} onclick={() => onCreate(teamName.trim())}>
          {busy ? 'Creating…' : 'Create a room'}
        </button>
        <div class="row2">
          <button class="btn-secondary" onclick={() => (mode = 'join')}>Join with a code</button>
          <button class="btn-ghost" onclick={onHome}>Back</button>
        </div>
      {:else}
        <button
          class="btn-primary"
          disabled={!named || joinCode.trim().length !== 6 || busy}
          onclick={() => onJoin(joinCode.trim().toUpperCase(), teamName.trim())}
        >
          {busy ? 'Joining…' : 'Join room'}
        </button>
        <div class="row2">
          <button class="btn-secondary" onclick={() => (mode = 'choose')}>Back</button>
          <button class="btn-ghost" onclick={onHome}>Home</button>
        </div>
      {/if}
    {:else if view.status === 'EXPIRED'}
      {#if view.you === 'host' && !view.extended}
        <button class="btn-primary" disabled={busy} onclick={onExtend}>Give it more time</button>
      {/if}
      <button class="btn-ghost" onclick={onHome}>Home</button>
    {:else if view.status === 'DONE'}
      <button class="btn-primary" onclick={onResult}>See the result</button>
      <button class="btn-ghost" onclick={onHome}>Home</button>
    {:else if view.status === 'WAITING'}
      <!--
        Sharing is the only thing that moves an empty room forward, so it leads
        even though drafting is also available. You can pick your side while you
        wait — async cuts both ways — but nobody joins a room they were never
        sent.
      -->
      <button class="btn-primary" onclick={share}>Share invite</button>
      <div class="row2">
        <button class="btn-secondary" disabled={you?.ready} onclick={onDraft}>
          {you?.ready ? 'Side locked in' : 'Pick your side'}
        </button>
        <button class="btn-ghost" onclick={onHome}>Home</button>
      </div>
    {:else if !you?.ready}
      <button class="btn-primary" onclick={onDraft}>Pick your side</button>
      <button class="btn-ghost" onclick={onHome}>Home</button>
    {:else if yourTurnToPlay}
      <button class="btn-primary" onclick={onPlay}>
        {#if inSuperOver}
          {view.target ? 'Chase it in one over' : 'Bat your super over'}
        {:else if view.status === 'INNINGS_2'}
          Start your chase
        {:else}
          Play your innings
        {/if}
      </button>
      <button class="btn-ghost" onclick={onHome}>Home</button>
    {:else}
      <button class="btn-ghost" onclick={onHome}>Home</button>
    {/if}
  </div>
</section>
